import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "./Toast";

interface UseConfirmDeleteOptions<T> {
  queryKey: readonly unknown[];
  mutationFn: (item: T) => Promise<unknown>;
  entityLabel: string;
  successMessage?: string;
  errorTitle?: string;
  onSuccess?: () => void;
  formatDescription?: (item: T) => string;
}

export const useConfirmDelete = <T,>({
  queryKey,
  mutationFn,
  entityLabel,
  successMessage,
  errorTitle = "No se pudo eliminar",
  onSuccess,
  formatDescription,
}: UseConfirmDeleteOptions<T>) => {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<T | null>(null);

  const remove = useMutation({
    mutationFn: (item: T) => mutationFn(item),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      showToast(
        "success",
        successMessage ?? `${entityLabel} eliminado`,
      );
      setPending(null);
      onSuccess?.();
    },
    onError: (err: Error) =>
      showToast("error", errorTitle, err.message),
  });

  return {
    pending,
    open: (item: T) => setPending(item),
    close: () => setPending(null),
    description: pending && formatDescription ? formatDescription(pending) : "",
    isLoading: remove.isPending,
    confirm: () => {
      if (!pending) return;
      remove.mutate(pending);
    },
  };
};